import { CommonTooltips, ImageTable } from "@mcleod/common";
import { BlurEvent, ChangeEvent, ClickEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DataSourceValidationEvent, DropdownSelectionEvent, Label, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { ArrayUtil, DateUtil, McLeodClassicIntegration, Model, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { getDispatchControlValue, LMEUserSettings, ResponsibilityUtil, UsersResponsibilitySlideoutMF } from "@mcleod/general";
import { AllocType } from "@mcleod/settlement/src/SegAllocDetailTable";
import { DuplicateId, DuplicateIdProps } from "../DuplicateId";
import { OrderTabView } from "../OrderTabView";
import { AutogenLayoutTractor } from "./autogen/AutogenLayoutTractor";
import { EquipmentAssignments } from "./EquipmentAssignments";
import { EquipmentItem } from "./EquipmentGroupValidation";
import { Seating, SeatingEquipmentType } from "./Seating";

const viewTractorURL = "lme/dispatch/asset/Tractor?mode=update&key=";
const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "tractor",
    screenName: "Tractor",
    navigateToUrl: viewTractorURL
};

export class Tractor extends AutogenLayoutTractor {
    buttonOpenClassic: any;
    private segAllocControl: ModelRow;

    override async onLoad() {
        this.segAllocControl = await Model.searchSingleRecord("lme/settlement/seg-alloc-control", null, { extraFields: "lme/dispatch/seg-alloc-control-single" });

        this.layoutTableComment.displayCopyOrderFields = false;
        this.layoutEquipmentIssued.setParentRowType(EquipmentItem.TRACTOR);
        this.layoutSegAllocDetail.allocType = AllocType.TRACTOR;
        this.sourceTractor.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceTractorAfterGetDataboundValues(row, dataSource);

        this.textboxPayOwner.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            const newValue = event.selection.value;
            this.textboxOwner.required = newValue === 'O' || newValue === 'B';
        });

        this.textboxDriver1Id.addBeforeLookupModelSearchListener((event) => {
            event.filter.only_active = true;
        });

        this.textboxDriver2Id.addBeforeLookupModelSearchListener((event) => {
            event.filter.only_active = true;
        });

        this.textboxDriver1Terminal.quickInfoOverrideKeyValue = () => {
            const id = this.mainDataSource?.activeRow?.get("driver1_terminal_id");
            return !StringUtil.isEmptyString(id) ? id : null;
        };

        this.textboxDriver2Terminal.quickInfoOverrideKeyValue = () => {
            const id = this.mainDataSource?.activeRow?.get("driver2_terminal_id");
            return !StringUtil.isEmptyString(id) ? id : null;
        };

        DuplicateId.configureIdTextbox(this.textId, DUPLICATE_VALIDATION_PROPS);

        this.setupOrderIdLabel();

        this.sourceTractor.addAfterModeChangeListener(async () => {
            if (this.sourceTractor.mode == DataSourceMode.SEARCH) {
                this.buttonTractorUsers.visible = await ResponsibilityUtil.isFilterEnabled("tractor_filter");
            }
            if (this.sourceTractor.mode == DataSourceMode.ADD) {
                this.buttonTractorUsers.visible = false;
            }
            if (this.sourceTractor.mode == DataSourceMode.UPDATE) {
                const controlValue = this.mainDataSource.activeRow.getString("tractor_filter_enabled");
                this.buttonTractorUsers.visible = controlValue === "Y";
            }
        });

    }

    private getTractorUsersAgencyField(): string {
        return "primary_agency";
    }

    /** This is an event handler for the beforeExecution event of sourceDriver.  It's gross. */
    sourceTractorAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set primary_agency in search mode because the field isn't bound
            const primaryAgencyField = this.getTractorUsersAgencyField();
            row.set(primaryAgencyField, dataSource.searchRow.get(primaryAgencyField));

            //have to add agency_link search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceAgencyLink.data))
                return;
            row.addLinkedModel({ model: this.sourceAgencyLink.url, rows: [this.sourceAgencyLink.data[0]] });

        }
    }

    textboxFleetIdOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const lookupModelData = textbox.getFirstLookupModelData();
        if (lookupModelData?.get("id") == lookupModelData?.get("name")) {
            this.activeRow.set("fleet_id", null);
        }
        else {
            this.activeRow.set("fleet_id", lookupModelData?.get("id"));
        }
    }

    textboxOwnerOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const lookupModelData = textbox.getFirstLookupModelData();
         if (lookupModelData?.get("id") == lookupModelData?.get("name")) {
            this.activeRow.set("owner", null);
         }
         else {
            this.activeRow.set("owner", lookupModelData?.get("id"));
         }
    }

    textboxDriver1IdOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const lookupModelData = textbox.getFirstLookupModelData();
        if (lookupModelData?.get("id") == lookupModelData?.get("name")) {
            this.activeRow?.set("driver1_id", null);
        }
        else {
            this.activeRow?.set("driver1_id", lookupModelData?.get("id"));
        }
        this.activeRow?.set("driver1_terminal_descr", "");
        this.activeRow?.set("assign_date", new Date());
        this.labelAssignedByInfo.text = "";
        this.clearDriver1TerminalTooltip();
    }

    clearDriver1TerminalTooltip() {
        this.textboxDriver1Terminal.tooltip = null;
    }

    setDriver1AssignedBy() {
        this.labelAssignedByInfo.text = this.mainDataSource?.activeRow?.get("driver1_assigned_by_info");
    }

    textboxDriver1OnDataDisplay() {
        this.setDriver1AssignedBy();
    }

    textboxDriver2IdOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const lookupModelData = textbox.getFirstLookupModelData();
        if (lookupModelData?.get("id") == lookupModelData?.get("name")) {
            this.activeRow?.set("driver2_id", null);
        }
        else {
            this.activeRow?.set("driver2_id", lookupModelData?.get("id"));
        }
        this.activeRow?.set("driver2_terminal_descr", "");
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.sf.EntryTractor", this.mainDataSource.activeRow.get("id"));
    }

    /** This is an event handler for the onClick event of labelPreassignment. */
    labelPreassignmentOnClick(event: ClickEvent) {
        const dispatchBy = getDispatchControlValue("dispatch_by");
        const equipmentId: string = dispatchBy == "T" ? this.activeRow.get("tractor.id") : this.activeRow.get("dispatch_assigned_driver.id");
        EquipmentAssignments.showSlideout({
            equipmentId: equipmentId,
            tractorId: this.activeRow.get("tractor.id"),
            movementId: this.activeRow.get("movement.id")
        });
    }

    sourceTractorBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.UPDATE || event.getAction() == DataSourceAction.ADD) {
            if (this.activeRow.getChangedData()?.hasOwnProperty("driver1_id")) {
                this.activeRow.set("dispatcher_login", LMEUserSettings.getUserId());
            }
        }
    }

    sourceTractorAfterExecution(event: DataSourceExecutionEvent) {
        const activeRow = this.mainDataSource.activeRow;
        const driver1Id:string = this.mainDataSource.activeRow.get("driver1_id");
        const driver2Id:string = this.mainDataSource.activeRow.get("driver2_id")

        if (driver1Id !== undefined) {
            CommonTooltips.setTooltipContentLayout(this.panelDriverOne, driver1Id, "lme/dispatch/asset/DriverQuickInfo", { minWidth: 128, minHeight: 140 });
        }

        if (driver2Id !== undefined) {
            CommonTooltips.setTooltipContentLayout(this.panelDriverTwo, driver2Id, "lme/dispatch/asset/DriverQuickInfo", { minWidth: 128, minHeight: 140 });
        }

        this.panelDriverTwo.visible = driver2Id !== undefined;

        if (event.getAction() == DataSourceAction.SEARCH || event.getAction() == DataSourceAction.UPDATE || event.getAction() == DataSourceAction.ADD) {
            const hasMovement = !this.activeRow?.isNull("movement.id");
            this.labelNoDispatchInfo.visible = !hasMovement;
            this.panelOrderId.visible = hasMovement;
            this.labelOrder.visible = hasMovement;
            this.textTrailerId.visible = hasMovement;
            this.textboxLastLocation.visible = hasMovement;
            this.panelAdditionalInfo.visible = hasMovement;
            this.layoutCurrentDispatchStops.visible = hasMovement;

            if (hasMovement) {
                this.layoutCurrentDispatchStops.mainDataSource.search({ id: this.activeRow?.get("movement.id") });
            }
            if (this.activeRow) {
                (this.layoutImageTable as ImageTable).populateForRow("T", this.activeRow?.get("id"));
            }
        }

        if (event.getAction() == DataSourceAction.UPDATE || event.getAction() == DataSourceAction.ADD) {
            this.textId.enabled = false;
            this.textId.required = true;
            this.panelOrderId.printable = true;
            this.textTrailerId.printable = true;
            this.textboxLastLocation.printable = true;
        }

        event.filter.parent_row_id = this.mainDataSource?.activeRow?.get("id");
        this.switchExcludeTripMgt.visible = event.data?.get("trip_management_licensed", false) === true;
        this.setInsuredFieldsRequired(event.data?.get("liability_insured", false) === "Y");

        if (!activeRow?.isNull("trailer2.id") || !activeRow?.isNull("trailer3.id")) {
            this.textTrailerId.caption = "Trailer 1";
        }

        if (!activeRow?.isNull("dolly1.id") && !activeRow?.isNull("dolly2.id")) {
            this.textDolly1Id.caption = "Dolly 1";
        }
    }


    sourceTractorBeforeModeChange(event) {
        this.buttonOpenClassic.visible = event.newMode === DataSourceMode.UPDATE;
        this.tabDispatch.visible = event.newMode !== DataSourceMode.ADD;

        if (event.newMode === DataSourceMode.ADD) {
            this.textId.enabledDuringAdd = true;
        }

        if (event.newMode === DataSourceMode.UPDATE) {
            this.textId.required = true;
            this.panelOrderId.visible = true;
        }

        if (event.newMode === DataSourceMode.SEARCH) {
            this.textboxLastLocation.visible = false;
            this.panelAdditionalInfo.visible = false;
            this.panelCurrentDispatchSearch.visible = true;
            this.layoutCurrentDispatchStops.visible = false;
        }
    }

    /** This is an event handler for the afterModeChange event of sourceEquipmentTrackingHist. */
    sourceEquipmentTrackingHistAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.SEARCH) {
            this.tabEquipmentTrackingHistory.visible = false;
        }
    }

    private setupOrderIdLabel() {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH)
            this.labelOrderId.addUnderlineWhenMouseOver();
    }

    public static navigateTo(id: string, newTab = true): void {
        Navigation.navigateTo(viewTractorURL + id, { newTab: newTab });
    }

    /** This is an event handler for the onRowDisplay event of tableEquipmentTrackingHist. */
    tableEquipmentTrackingHistOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const labelLocationName = tableRow.findComponentById("labelLocationName") as Label;
        labelLocationName.quickInfoOverrideKeyValue = tableRow.data.get("location_id");
    }

    sourceTractorAfterModeChange(event: DataSourceModeChangeEvent) {
        this.tabImage.visible = this.tabDispatch.visible = event.newMode == DataSourceMode.UPDATE;
        this.buttonLocate.visible = event.newMode == DataSourceMode.UPDATE;
        this.setAllocationVisible();
    }

    /** This is an event handler for the onClick event of labelOrderId. */
    labelOrderIdOnClick(event: ClickEvent) {
        const orderId = this.activeRow?.get("movement_order.order_id");
        OrderTabView.navigateTo({ key: orderId, newTab: true })
    }

    /** This is an event handler for the onClick event of buttonTractorUsers. */
    buttonTractorUsersOnClick(event: ClickEvent) {
        const urs = UsersResponsibilitySlideoutMF.builder()
            .layoutName("lme/dispatch/asset/TractorUsers")
            .title(this.getTractorUsersSlideoutTitle())
            .idFilter(this.getTractorUsersIdFilter())
            .parentMainDataSource(this.sourceTractor)
            .parentAgencyLinkDataSource(this.sourceAgencyLink)
            .primaryAgencyField(this.getTractorUsersAgencyField())
            .build();
        urs.show();
    }

    private getTractorUsersSlideoutTitle(): string {
        const name = this.sourceTractor.activeRow?.get("id");
        return "Tractor Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getTractorUsersIdFilter(): any {
        const id = this.sourceTractor.activeRow?.get("id");
        return id ? { id: id } : null;
    }

    private setAllocationVisible() {
        this.tabAllocation.visible = this.segAllocControl.getBoolean("is_active", false);
    }

    buttonLocateOnClick(event: ClickEvent) {
        Seating.showSlideout({
            equipmentId: this.activeRow.get("id"),
            equipmentType: SeatingEquipmentType.TRACTOR,
            doAfterSave: () => this.mainDataSource.search({ id: this.activeRow.get("id") })
        });
    }

    /** This is an event handler for the onChange event of switchInsured. */
    switchInsuredOnChange(event: ChangeEvent) {
        this.setInsuredFieldsRequired(event.newValue);
    }

    setInsuredFieldsRequired(enabled: boolean) {
        this.panelInsurance.forEveryChildComponent((component) => {
            if (component instanceof Textbox) {
                component.required = enabled;
            }
        });
    }

    /** This is an event handler for the onValidate event of sourceTractor. */
    sourceTractorOnValidate(event: DataSourceValidationEvent) {
        const row = this.activeRow;
        const startDate: Date = DateUtil.parseDate(row.get("liability_start_dt"));
        const endDate: Date = DateUtil.parseDate(row.get("liability_end_dt"));
        if (startDate && endDate && startDate > endDate) {
            const errorMessage = "Liability Start Date must be before Liability End Date.";
            event.validationResults.push({ isValid: false, validationMessage: errorMessage, caption: errorMessage, component: this.textboxLiabilityStartDt });
        }
    }
}
